import { buildAuthorizeUrl, parseCallback, pkceChallenge, exchangeCode, type AuthConfig } from "./auth";
import {
  getJSON, postJSON, setToken, clearToken, isOperator, tokenExpired, SessionExpiredError,
} from "./api";
import { effectiveLimit, usagePct, statusOf, money, type Row } from "./render";

// Written by the deploy step into config.js; see the runbook.
const cfg = (window as any).GUARDRAILS_CONFIG as AuthConfig;

const app = document.getElementById("app")!;
const who = document.getElementById("who")!;

function el(tag: string, attrs: Record<string, string> = {}, ...kids: (Node | string)[]): HTMLElement {
  const n = document.createElement(tag);
  for (const [k, v] of Object.entries(attrs)) if (v !== "") n.setAttribute(k, v);
  n.append(...kids);
  return n;
}

const msg = (e: unknown) => String((e as Error)?.message ?? e);

/**
 * Fatal only — boot failures, where there is no page to keep. An action that
 * fails must NOT come through here: replaceChildren wipes the dashboard, which
 * reads as being thrown onto a blank error page for something as ordinary as a
 * 403 on a button you were not allowed to press.
 */
const fatal = (e: unknown) =>
  e instanceof SessionExpiredError
    ? signInScreen("Your session expired.")
    : app.replaceChildren(el("div", { class: "err" }, msg(e)));

/** In-place notice at the top of the page; the dashboard stays where it is. */
function notify(text: string, danger = false): void {
  app.querySelector(".notice")?.remove();
  const n = el("div", { class: danger ? "note note-danger notice" : "note notice" }, text);
  app.prepend(n);
  n.scrollIntoView({ block: "nearest" });
}

/**
 * An action failed: say so in red, in place, and leave the page alone.
 *
 * Except an expired session — there is nothing left to leave alone, every
 * subsequent call would fail the same way, and a red notice above a dashboard
 * of stale numbers invites the reader to trust them.
 */
const actionFailed = (e: unknown) =>
  e instanceof SessionExpiredError
    ? signInScreen("Your session expired.")
    : notify(msg(e), true);

/** Success message surviving the re-render that follows an action. */
let pending: string | null = null;
const banner = (text: string) => { pending = text; };

// ---- auth ------------------------------------------------------------------

/**
 * The one way back to a signed-out state. It CLEARS STORAGE, which is the point:
 * a dead id_token left in sessionStorage is picked straight back up by boot(),
 * so without this the page cannot recover from an expired session by reloading
 * — only by closing the tab, which is what discards sessionStorage.
 *
 * Also drops any half-finished pkce/state pair, so an abandoned sign-in cannot
 * collide with the next one.
 */
function signInScreen(notice?: string): void {
  clearToken();
  sessionStorage.clear();
  who.replaceChildren();
  const b = el("button", { class: "btn btn-primary" }, "Sign in with Entra ID");
  b.addEventListener("click", () => void signIn().catch(fatal));
  app.replaceChildren(
    ...(notice ? [el("div", { class: "note note-danger" }, notice)] : []),
    el("div", { style: "margin-top:12px" }, b));
}

async function signIn(): Promise<void> {
  const { verifier, challenge } = await pkceChallenge();
  const state = crypto.randomUUID();
  sessionStorage.setItem("pkce", verifier);
  sessionStorage.setItem("state", state);
  location.href = buildAuthorizeUrl(cfg, challenge, state);
}

/**
 * Group membership is baked into the token when it is issued, so someone added to
 * guardrails-operators keeps getting 403 until they get a NEW token. Dropping the
 * local copy is not enough — the Cognito session would mint the same claims — so
 * this goes through the hosted logout endpoint.
 */
function signOut(): void {
  sessionStorage.clear();
  const u = new URL(cfg.cognitoDomain + "/logout");
  u.searchParams.set("client_id", cfg.clientId);
  u.searchParams.set("logout_uri", cfg.redirectUri);
  location.href = u.toString();
}

async function boot(): Promise<void> {
  const cb = parseCallback(location.search);
  if (cb) {
    if (!cb.state || cb.state !== sessionStorage.getItem("state")) {
      throw new Error("Sign-in state mismatch — start again.");
    }
    const verifier = sessionStorage.getItem("pkce");
    if (!verifier) throw new Error("Sign-in verifier missing — start again.");
    const token = await exchangeCode(cfg, cb.code, verifier);
    setToken(token);
    sessionStorage.setItem("id_token", token);
    sessionStorage.removeItem("pkce");
    sessionStorage.removeItem("state");
    history.replaceState({}, "", "/");
  } else {
    const stored = sessionStorage.getItem("id_token");
    // An hour-old token is the normal case for a tab reopened later, so check
    // `exp` here rather than spending a round trip to be told 401.
    if (!stored || tokenExpired(stored)) {
      signInScreen(stored ? "Your session expired." : undefined);
      return;
    }
    setToken(stored);
  }
  await renderDay(new Date().toISOString().slice(0, 10));
}

// ---- shared chrome ---------------------------------------------------------

let currentDay = new Date().toISOString().slice(0, 10);
let currentMonth = currentDay.slice(0, 7);
let view: "day" | "month" | "audit" = "day";

/** Day / Month / Audit tabs, underlined in the primary like the Atoti menu
 *  convention. Audit is offered only to operators — the API refuses everyone
 *  else regardless, so this only avoids showing a button that cannot work. */
function tabs(): HTMLElement {
  const tab = (label: string, active: boolean, go: () => void) => {
    const b = el("button", {
      class: "btn btn-ghost",
      style: active ? "color:#000;font-weight:500;box-shadow:inset 0 -2px 0 var(--primary)" : "",
    }, label);
    if (!active) b.addEventListener("click", go);
    return b;
  };
  const row = el("div", { style: "display:flex;gap:2px" },
    tab("Day", view === "day", () => void renderDay(currentDay).catch(actionFailed)),
    tab("Month", view === "month", () => void renderMonth(currentMonth).catch(actionFailed)));
  if (isOperator()) {
    row.append(tab("Audit", view === "audit",
      () => void renderAudit(currentDay).catch(actionFailed)));
  }
  return row;
}

function header(right: HTMLElement): HTMLElement {
  who.replaceChildren();
  const out = el("button", { class: "btn btn-ghost" }, "Sign out");
  out.addEventListener("click", signOut);
  who.append(out);
  return right;
}

// ---- the day view ----------------------------------------------------------

function tile(k: string, v: string, s: string): HTMLElement {
  return el("div", { class: "tile" },
    el("div", { class: "k" }, k), el("div", { class: "v" }, v), el("div", { class: "s" }, s));
}

async function renderDay(utcDay: string): Promise<void> {
  view = "day";
  currentDay = utcDay;
  app.replaceChildren("Loading…");

  const data = await getJSON(`/api/day/${utcDay}`);
  const rows: Row[] = data.rows;
  const def: number = data.defaultThresholdUsd;
  const isToday = utcDay === new Date().toISOString().slice(0, 10);

  header(el("span"));

  const total = rows.reduce((a, r) => a + r.usd, 0);
  const over = rows.filter((r) => statusOf(r, def).label === "Over limit").length;
  const susp = rows.filter((r) => r.suspended).length;

  // day navigator
  const shift = (days: number) => {
    const d = new Date(utcDay + "T00:00:00Z");
    d.setUTCDate(d.getUTCDate() + days);
    return d.toISOString().slice(0, 10);
  };
  const prev = el("button", { class: "btn" }, "‹");
  prev.addEventListener("click", () => void renderDay(shift(-1)).catch(actionFailed));
  const next = el("button", { class: "btn" }, "›");
  if (isToday) next.setAttribute("disabled", "disabled");
  else next.addEventListener("click", () => void renderDay(shift(1)).catch(actionFailed));

  const today = el("button", { class: "btn btn-ghost" }, "Jump to today");
  if (isToday) today.setAttribute("disabled", "disabled");
  else today.addEventListener("click", () =>
    void renderDay(new Date().toISOString().slice(0, 10)).catch(actionFailed));

  // Today's figures move while you look at them, so say how old these are.
  const loadedAt = new Date();
  const stamp = el("span", { class: "muted" },
    `updated ${loadedAt.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", second: "2-digit" })}`);

  const refresh = el("button", { class: "btn" }, "Refresh");
  refresh.addEventListener("click", () => {
    // Disable while in flight: a second click would race two renders and the
    // slower response would win, showing older numbers than you already had.
    refresh.setAttribute("disabled", "disabled");
    refresh.textContent = "Refreshing…";
    void renderDay(utcDay).catch((e) => {
      refresh.removeAttribute("disabled");
      refresh.textContent = "Refresh";
      actionFailed(e);
    });
  });

  const nav = el("div", { style: "display:flex;align-items:center;gap:12px" },
    tabs(),
    el("div", { style: "width:1px;height:16px;background:var(--border)" }),
    el("div", { style: "display:flex;gap:4px" }, prev, next),
    el("div", { style: "font-size:20px;font-weight:600" }, utcDay),
    isToday
      ? el("span", { class: "badge badge-info" }, "Today · partial")
      : el("span", { class: "badge" }, "Closed day"),
    el("div", { style: "flex-grow:1" }),
    stamp,
    refresh,
    today);

  const head = el("tr", {},
    el("th", {}, "Principal"), el("th", {}, "Kind"),
    el("th", { class: "num" }, "Spend"), el("th", { class: "num" }, "Effective limit"),
    el("th", {}, "Usage"), el("th", {}, "Status"), el("th", { class: "num" }, "Actions"));

  const body = rows.map((r) => {
    const limit = effectiveLimit(r, def);
    const pct = Math.min(100, usagePct(r, def));
    const st = statusOf(r, def);

    const bar = el("div", { class: "bar" });
    bar.append(el("i", { style: `width:${pct.toFixed(1)}%`, class: st.danger ? "over" : "" }));

    const act = el("td", { class: "num", style: "white-space:nowrap" });
    const panelCell = el("td", { colspan: "7", style: "padding:0" });
    const panelRow = el("tr", {}, panelCell);
    panelRow.hidden = true;

    const closePanel = () => { panelCell.replaceChildren(); panelRow.hidden = true; };
    const openPanel = (build: () => HTMLElement) => {
      // Only one panel open at a time, as on the canvas.
      app.querySelectorAll("tr[data-panel]").forEach((n) => {
        if (n !== panelRow) { (n as HTMLElement).hidden = true; n.firstElementChild!.replaceChildren(); }
      });
      panelCell.replaceChildren(build());
      panelRow.hidden = false;
    };
    panelRow.setAttribute("data-panel", "");

    if (isToday) {
      const raise = el("button", { class: "btn" }, "Raise limit");
      raise.addEventListener("click", () =>
        panelRow.hidden ? openPanel(() => limitPanel(r, def, closePanel)) : closePanel());
      const kill = el("button",
        { class: r.suspended ? "btn" : "btn btn-danger" },
        r.suspended ? "Release" : "Kill switch");
      kill.addEventListener("click", () => { closePanel(); void doKill(r).catch(actionFailed); });
      act.append(raise, " ", kill);
    } else {
      act.append(el("span", { class: "muted" }, "Day closed"));
    }

    const nameCell = el("td", {});
    const open = el("button", { class: "btn btn-ghost", style: "padding:0;height:auto;border:0" }, r.name);
    open.addEventListener("click", () => void renderPrincipal(utcDay, r.key).catch(actionFailed));
    nameCell.append(open);

    return [
      el("tr", {},
        nameCell,
        el("td", {}, el("span", { class: "badge" }, r.kind)),
        el("td", { class: "num" }, money(r.usd)),
        el("td", { class: "num" }, limit === null ? "No cap" : money(limit)),
        el("td", { style: "width:220px" }, bar),
        el("td", {}, el("span", { class: st.danger ? "badge badge-danger" : "badge" }, st.label)),
        act),
      panelRow,
    ];
  }).flat();

  const table = rows.length
    ? el("table", {}, el("thead", {}, head), el("tbody", {}, ...body))
    : el("div", { style: "padding:48px 16px;text-align:center" },
        el("div", { style: "font-weight:500" }, "No Bedrock calls on this day"),
        el("div", { class: "muted" }, "No principal invoked a metered model. Nothing to enforce."));

  const note = pending ? el("div", { class: "note" }, pending) : el("span", {});
  pending = null;

  app.replaceChildren(
    nav,
    note,
    el("div", { class: "tiles" },
      tile("Spend this day", money(total),
        isToday ? "partial day, still accruing" : "final for this UTC day"),
      tile("Principals active", String(rows.length),
        rows.length === 0 ? "no metered calls this day" : "invoked a model"),
      tile("Over their limit", String(over),
        over === 0 ? "all within their limit" : "at or past the limit"),
      tile("Suspended now", String(susp), "Bedrock denied until 00:05 UTC")),
    el("div", { class: "card" },
      el("div", { class: "card-h" },
        el("span", {}, "Spend and enforcement"),
        el("span", { class: "muted" }, `Default cap ${money(def)} per principal per UTC day`)),
      el("div", { style: "padding:4px 16px 12px" }, table)));
}

// ---- the month view --------------------------------------------------------

const MONTH_NAMES = ["January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December"];

// ---- the daily bar chart, shared by the month and principal views ----------

type BarChart = {
  /** Every column, in order. Gaps must already be filled in by the caller. */
  days: string[];
  /** Value mapped to full column height. */
  max: number;
  value: (day: string) => number;
  fill: (day: string, v: number) => string;
  /** Same as `fill` to leave a column inert on hover. */
  hoverFill: (day: string, v: number) => string;
  /** [the figure, the qualifier under it] */
  tip: (day: string, v: number) => [string, string];
  label: (day: string, i: number) => string;
  height?: number;
};

/**
 * Returns the whole chart as ONE positioned element — the tooltip is absolutely
 * placed against `col.offsetParent`, so the wrapper has to travel with the
 * columns. Handing back a fragment would resolve offsetParent to whatever the
 * caller happened to nest it in, and the tooltip would land somewhere else on
 * one view and not the other.
 *
 * Bars and labels are SEPARATE rows. Putting the label inside each column made
 * every bar's baseline depend on whether that column had a label, so even days
 * sat a line-height lower than odd ones.
 */
function barChart(o: BarChart): HTMLElement {
  const h = o.height ?? 180;

  // Hover layer. The hit target is the full-height column, not the bar, so a
  // near-zero day is still reachable.
  const tip = el("div", { class: "tip", role: "tooltip" });
  tip.hidden = true;

  const bars = o.days.map((day) => {
    const v = o.value(day);
    const pct = o.max > 0 ? (v / o.max) * 100 : 0;
    const base = o.fill(day, v);

    const mark = el("div", {
      style: `width:100%;height:${pct.toFixed(1)}%;min-height:${v > 0 ? "2px" : "1px"};` +
        `background:${base};border-radius:2px 2px 0 0;transition:background-color .15s`,
    });
    const col = el("div", {
      style: `flex:1;height:${h}px;display:flex;align-items:flex-end;cursor:default`,
      tabindex: "0",
      "aria-label": `${day}: ${money(v)}`,
    });
    col.append(mark);

    const show = () => {
      const [primary, qualifier] = o.tip(day, v);
      tip.replaceChildren(
        el("div", { style: "font-weight:600;font-variant-numeric:tabular-nums" }, primary),
        el("div", { class: "muted", style: "color:#DBDBDB" }, qualifier));
      tip.hidden = false;
      const r = col.getBoundingClientRect();
      const host = col.offsetParent as HTMLElement;
      const hr = host.getBoundingClientRect();
      tip.style.left = `${r.left - hr.left + r.width / 2}px`;
      tip.style.top = `${r.top - hr.top - 8}px`;
      mark.style.background = o.hoverFill(day, v);
    };
    const hide = () => {
      tip.hidden = true;
      mark.style.background = base;
    };
    col.addEventListener("mouseenter", show);
    col.addEventListener("mouseleave", hide);
    col.addEventListener("focus", show);
    col.addEventListener("blur", hide);
    return col;
  });

  const labels = o.days.map((day, i) =>
    el("div", { class: "muted", style: "flex:1;text-align:center;font-size:10px" },
      o.label(day, i)));

  return el("div", { style: "padding:16px;position:relative" },
    tip,
    el("div", { style: "display:flex;gap:3px;align-items:flex-end" }, ...bars),
    el("div", { style: "display:flex;gap:3px;padding-top:4px" }, ...labels));
}

/** Every date from `from` to `to` inclusive, so gaps become zero columns. */
function daySpan(from: string, to: string): string[] {
  const out: string[] = [];
  const d = new Date(from + "T00:00:00Z");
  const end = new Date(to + "T00:00:00Z");
  while (d <= end) {
    out.push(d.toISOString().slice(0, 10));
    d.setUTCDate(d.getUTCDate() + 1);
  }
  return out;
}

function monthLabel(m: string): string {
  const [y, mo] = m.split("-").map(Number);
  return `${MONTH_NAMES[mo - 1]} ${y}`;
}

function shiftMonth(m: string, by: number): string {
  const [y, mo] = m.split("-").map(Number);
  const d = new Date(Date.UTC(y, mo - 1 + by, 1));
  return d.toISOString().slice(0, 7);
}

async function renderMonth(month: string): Promise<void> {
  view = "month";
  currentMonth = month;
  app.replaceChildren("Loading…");

  const d = await getJSON(`/api/month/${month}`);
  const rows = d.rows as {
    key: string; kind: string; name: string; usd: number;
    peakUsd: number; peakDay: string; activeDays: number;
  }[];
  const daily = d.daily as { utcDay: string; totalUsd: number }[];
  const def: number = d.defaultThresholdUsd;
  const thisMonth = new Date().toISOString().slice(0, 7);
  const isCurrent = month === thisMonth;

  header(el("span"));

  const mtd = rows.reduce((a, r) => a + r.usd, 0);
  const avg = d.elapsedDays > 0 ? mtd / d.elapsedDays : 0;

  const prev = el("button", { class: "btn" }, "‹");
  prev.addEventListener("click", () => void renderMonth(shiftMonth(month, -1)).catch(actionFailed));
  const next = el("button", { class: "btn" }, "›");
  if (month >= thisMonth) next.setAttribute("disabled", "disabled");
  else next.addEventListener("click", () => void renderMonth(shiftMonth(month, 1)).catch(actionFailed));

  const nav = el("div", { style: "display:flex;align-items:center;gap:12px" },
    tabs(),
    el("div", { style: "width:1px;height:16px;background:var(--border)" }),
    el("div", { style: "display:flex;gap:4px" }, prev, next),
    el("div", { style: "font-size:20px;font-weight:600" }, monthLabel(month)),
    isCurrent
      ? el("span", { class: "badge badge-info" },
          `Month to date · ${d.elapsedDays} of ${d.daysInMonth} days`)
      : el("span", { class: "badge" }, `${d.daysInMonth} days`));

  // daily columns across the whole month
  const byDay = new Map(daily.map((x) => [x.utcDay, x.totalUsd]));
  const days = Array.from({ length: d.daysInMonth }, (_, i) =>
    `${month}-${String(i + 1).padStart(2, "0")}`);
  const peak = Math.max(0, ...daily.map((x) => x.totalUsd));
  const elapsed = (day: string) => Number(day.slice(8)) <= d.elapsedDays;

  const chart = barChart({
    days,
    max: Math.max(peak * 1.15, def * 1.2),
    value: (day) => byDay.get(day) ?? 0,
    fill: (day, v) => !elapsed(day) ? "#F0F0F0" : (v === peak && v > 0 ? "#7194B9" : "#5B738D"),
    hoverFill: (day, v) => !elapsed(day) ? "#F0F0F0"
      : (v === peak && v > 0 ? "#5B738D" : "#4C5E70"),
    tip: (day, v) => [
      money(v),
      `${Number(day.slice(8))} ${MONTH_NAMES[Number(month.slice(5)) - 1]}` +
        (!elapsed(day) ? " · not yet elapsed" : v === 0 ? " · no metered calls" : ""),
    ],
    label: (_day, i) => ((i + 1) % 2 === 1 ? String(i + 1) : ""),
  });

  const mx = rows.length ? rows[0].usd : 1;
  const body = rows.slice(0, 15).map((r, i) => {
    const over = r.peakUsd >= def;
    const share = mtd > 0 ? (r.usd / mtd) * 100 : 0;
    const bar = el("div", { class: "bar", style: "width:200px" });
    bar.append(el("i", { style: `width:${((r.usd / mx) * 100).toFixed(1)}%` }));
    return el("tr", {},
      el("td", { class: "num muted", style: "width:28px" }, String(i + 1)),
      el("td", {}, r.name),
      el("td", {}, el("span", { class: "badge" }, r.kind)),
      el("td", { class: "num", style: "font-weight:600" }, money(r.usd)),
      el("td", {}, bar),
      el("td", { class: "num muted" }, `${share.toFixed(1)}%`),
      el("td", { class: "num", style: over ? "color:var(--danger-ink);font-weight:600" : "" },
        money(r.peakUsd)),
      el("td", { class: "num muted" }, r.peakDay.slice(8)),
      el("td", { class: "num muted" }, `${r.activeDays} / ${d.elapsedDays}`));
  });

  const rest = rows.slice(15);
  if (rest.length) {
    body.push(el("tr", {},
      el("td", {}), el("td", { class: "muted", colspan: "2" }, `${rest.length} further principals`),
      el("td", { class: "num muted" }, money(rest.reduce((a, r) => a + r.usd, 0))),
      el("td", { colspan: "5" })));
  }

  // Separator BETWEEN rows only: a border-top on the first drew a second line
  // immediately under the card header's own border-bottom.
  const breaches = (d.breaches as { name: string; utcDay: string; usd: number }[]).map((b, i) =>
    el("div", { style: "display:flex;justify-content:space-between;gap:8px;padding:8px 0"
      + (i > 0 ? ";border-top:1px solid var(--divider)" : "") },
      el("span", {}, el("strong", {}, b.name),
        el("span", { class: "muted", style: "margin-left:8px" },
          `${Number(b.utcDay.slice(8))} ${MONTH_NAMES[Number(month.slice(5)) - 1]}`)),
      el("span", { class: "num", style: "font-weight:600;color:var(--danger-ink)" }, money(b.usd))));

  app.replaceChildren(
    nav,
    el("div", { class: "tiles" },
      tile("Spend this month", money(mtd),
        isCurrent ? `${d.elapsedDays} days elapsed of ${d.daysInMonth}` : `${d.daysInMonth} days`),
      tile("Daily average", money(avg), "across elapsed days"),
      tile("Principals active", String(rows.length), "invoked a metered model"),
      tile("Days over the cap", String(d.breaches.length), `${money(def)} per principal per UTC day`)),
    el("div", { class: "card" },
      el("div", { class: "card-h" }, el("span", {}, "Spend per UTC day"),
        el("span", { class: "muted" }, "All principals combined")),
      chart),
    el("div", { style: "display:flex;gap:16px;align-items:flex-start" },
      el("div", { class: "card", style: "flex-grow:1" },
        el("div", { class: "card-h" }, el("span", {}, "Principals by spend this month"),
          el("span", { class: "muted" }, `Top ${Math.min(15, rows.length)} of ${rows.length}`)),
        el("div", { style: "padding:4px 16px 12px" },
          rows.length
            ? el("table", {},
                el("thead", {}, el("tr", {},
                  el("th", { class: "num" }, "#"), el("th", {}, "Principal"), el("th", {}, "Kind"),
                  el("th", { class: "num" }, "Month"), el("th", {}, "Share"),
                  el("th", { class: "num" }, "% of all"), el("th", { class: "num" }, "Peak"),
                  el("th", { class: "num" }, "Peak day"), el("th", { class: "num" }, "Active days"))),
                el("tbody", {}, ...body))
            : el("div", { style: "padding:32px;text-align:center" },
                el("div", { style: "font-weight:500" }, "No metered spend this month")))),
      el("div", { class: "card", style: "width:320px;flex:0 0 320px" },
        el("div", { class: "card-h" }, el("span", {}, "Over the cap"),
          el("span", { class: "muted" }, "This month")),
        el("div", { style: "padding:4px 16px 12px" },
          ...(breaches.length ? breaches : [el("div", { class: "muted", style: "padding:8px 0" }, "None.")]),
          el("div", { class: "muted", style: "border-top:1px solid var(--divider);padding-top:10px;margin-top:10px" },
            "Derived from stored daily totals, so it covers the whole month. The SUSPENDED rows that record actual enforcement are cleared nightly.")))));
}

// ---- principal drill-down --------------------------------------------------

const compact = (n: number) =>
  n >= 1e9 ? (n / 1e9).toFixed(1) + "B"
    : n >= 1e6 ? (n / 1e6).toFixed(1) + "M"
      : n >= 1e3 ? (n / 1e3).toFixed(1) + "k" : String(n);

async function renderPrincipal(utcDay: string, key: string): Promise<void> {
  app.replaceChildren("Loading…");
  const d = await getJSON(`/api/principal/${utcDay}/${encodeURIComponent(key)}`);
  const def: number = d.defaultThresholdUsd;
  const limit: number | null = d.limitUsd === undefined ? def : d.limitUsd;
  const models = d.models as {
    modelId: string; usd: number; calls: number;
    input: number; output: number; cacheRead: number; cacheWrite: number;
  }[];
  const daily = d.daily as { utcDay: string; totalUsd: number }[];

  header(el("span"));

  const back = el("button", { class: "btn" }, `‹ Back to ${utcDay}`);
  back.addEventListener("click", () => void renderDay(utcDay).catch(actionFailed));

  const st = statusOf(
    { key, kind: d.kind, name: d.name, usd: d.usd, limitUsd: d.limitUsd, suspended: d.suspended },
    def);

  const nav = el("div", { style: "display:flex;align-items:center;gap:12px" },
    back,
    el("div", { style: "font-size:20px;font-weight:600" }, d.name),
    el("span", { class: "badge" }, d.kind),
    el("span", { class: st.danger ? "badge badge-danger" : "badge" }, st.label));

  // Cache efficiency is the reason two principals with similar call counts can
  // differ by an order of magnitude in cost, so it gets a tile of its own.
  const input = models.reduce((a, m) => a + m.input, 0);
  const cacheRead = models.reduce((a, m) => a + m.cacheRead, 0);
  const calls = models.reduce((a, m) => a + m.calls, 0);
  const cachePct = input + cacheRead > 0 ? (cacheRead / (input + cacheRead)) * 100 : 0;
  const peak = daily.reduce((a, x) => (x.totalUsd > a.totalUsd ? x : a),
    { utcDay: "—", totalUsd: 0 });

  // Every day in the window, not just the ones with spend: a gap says "this
  // principal was quiet", which is exactly what you are looking for on a page
  // about runaway usage, and the old chart rendered it as no column at all.
  //
  // The window ends TODAY rather than at the drilled-in day — days after the
  // one you came from are still real spend, and the tiles above already count
  // them. It is capped at 30 columns so the chart cannot grow without bound as
  // the table fills up, and clamped to the first day the table holds so a young
  // table does not open with a fortnight of dead space.
  const WINDOW_DAYS = 30;
  const today = new Date().toISOString().slice(0, 10);
  const windowStart = new Date(Date.now() - (WINDOW_DAYS - 1) * 86400000)
    .toISOString().slice(0, 10);
  const firstDay: string = d.firstDay ?? daily[0]?.utcDay ?? today;
  const days = daySpan(windowStart > firstDay ? windowStart : firstDay, today);

  const byDay = new Map(daily.map((x) => [x.utcDay, x.totalUsd]));
  const dayPeak = Math.max(0, ...daily.map((x) => x.totalUsd));

  // A breach outranks the "you are looking at this day" highlight: which day is
  // selected is navigation, going over the cap is the thing the page is for.
  const shade = (day: string, v: number, hover: boolean): string =>
    v >= def ? (hover ? "#C9484A" : "#E15759")
      : day === utcDay ? (hover ? "#5B738D" : "#7194B9")
        : (hover ? "#4C5E70" : "#5B738D");

  const chart = barChart({
    days,
    max: Math.max(dayPeak * 1.15, def * 1.2),
    value: (day) => byDay.get(day) ?? 0,
    fill: (day, v) => shade(day, v, false),
    hoverFill: (day, v) => shade(day, v, true),
    tip: (day, v) => {
      const notes = [
        v === 0 ? "no metered calls" : null,
        v >= def ? `over the ${money(def)} cap` : null,
        day === utcDay ? "shown in the table below" : null,
      ].filter(Boolean);
      return [
        money(v),
        `${Number(day.slice(8))} ${MONTH_NAMES[Number(day.slice(5, 7)) - 1]}` +
          (notes.length ? ` · ${notes.join(" · ")}` : ""),
      ];
    },
    label: (day, i) => (i % 2 === 0 ? `${day.slice(8)}/${day.slice(5, 7)}` : ""),
  });

  const modelRows = models.map((m) => el("tr", {},
    el("td", { style: "font-weight:500" }, m.modelId),
    el("td", { class: "num" }, m.calls.toLocaleString("en-US")),
    el("td", { class: "num" }, compact(m.input)),
    el("td", { class: "num" }, compact(m.output)),
    el("td", { class: "num", style: m.cacheRead === 0 ? "color:var(--danger-ink)" : "" },
      compact(m.cacheRead)),
    el("td", { class: "num", style: "font-weight:600" }, money(m.usd))));

  const modelTable = models.length
    ? el("table", {},
        el("thead", {}, el("tr", {},
          el("th", {}, "Model"), el("th", { class: "num" }, "Calls"),
          el("th", { class: "num" }, "Input"), el("th", { class: "num" }, "Output"),
          el("th", { class: "num" }, "Cache reads"), el("th", { class: "num" }, "Cost"))),
        el("tbody", {}, ...modelRows))
    : el("div", { style: "padding:32px;text-align:center" },
        el("div", { style: "font-weight:500" }, "No per-model data for this day"),
        el("div", { class: "muted" },
          "The breakdown is recorded from 2026-09-11 onward; earlier days have totals only."));

  app.replaceChildren(
    nav,
    el("div", { class: "tiles" },
      tile("Spend, all days", money(d.totalUsd), `${d.activeDays} active days`),
      tile("Peak day", money(peak.totalUsd), peak.utcDay),
      tile("Effective limit", limit === null ? "No cap" : money(limit),
        d.limitUsd === undefined ? "global default" : "override today"),
      tile("Cached input", calls ? `${cachePct.toFixed(0)}%` : "—",
        "of input tokens served from cache")),
    el("div", { class: "card" },
      el("div", { class: "card-h" }, el("span", {}, "Daily spend"),
        el("span", { class: "muted" },
          `${days.length} days · ${days[0]} to ${days[days.length - 1]}`)),
      chart),
    el("div", { class: "card" },
      el("div", { class: "card-h" }, el("span", {}, "By model"),
        el("span", { class: "muted" }, utcDay)),
      el("div", { style: "padding:4px 16px 12px" }, modelTable)));
}

// ---- the audit view (operator only) ----------------------------------------

type AuditEntry = {
  utcDay: string; action: string; principalKey: string; actor: string;
  reason: string; before?: string; after?: string; selfService?: boolean; sk?: string;
};

/** Colour follows what the action DOES, not how recent it is. */
const ACTION_STYLE: Record<string, string> = {
  suspend: "border-color:var(--danger);color:var(--danger-ink)",
  release: "",
  setLimit: "border-color:var(--primary);color:var(--primary-ink);background:var(--primary-bg)",
  clearLimit: "",
  // A restated total is not an enforcement action, so not danger — but it moves
  // the number enforcement reads, so not a plain badge either.
  correctTotal: "border-color:var(--mark);color:var(--mark)",
};

async function renderAudit(utcDay: string): Promise<void> {
  view = "audit";
  currentDay = utcDay;
  app.replaceChildren("Loading…");

  const d = await getJSON(`/api/audit/${utcDay}`);
  const entries = (d.entries as AuditEntry[])
    .slice()
    .sort((a, b) => String(b.sk ?? "").localeCompare(String(a.sk ?? "")));
  const isToday = utcDay === new Date().toISOString().slice(0, 10);

  header(el("span"));

  const shift = (days: number) => {
    const t = new Date(utcDay + "T00:00:00Z");
    t.setUTCDate(t.getUTCDate() + days);
    return t.toISOString().slice(0, 10);
  };
  const prev = el("button", { class: "btn" }, "‹");
  prev.addEventListener("click", () => void renderAudit(shift(-1)).catch(actionFailed));
  const next = el("button", { class: "btn" }, "›");
  if (isToday) next.setAttribute("disabled", "disabled");
  else next.addEventListener("click", () => void renderAudit(shift(1)).catch(actionFailed));

  const nav = el("div", { style: "display:flex;align-items:center;gap:12px" },
    tabs(),
    el("div", { style: "width:1px;height:16px;background:var(--border)" }),
    el("div", { style: "display:flex;gap:4px" }, prev, next),
    el("div", { style: "font-size:20px;font-weight:600" }, utcDay),
    isToday ? el("span", { class: "badge badge-info" }, "Today") : el("span", { class: "badge" }, "Closed day"),
    el("div", { style: "flex-grow:1" }),
    el("span", { class: "muted" }, "Operators only"));

  const actors = [...new Set(entries.map((e) => e.actor))];
  const principals = [...new Set(entries.map((e) => e.principalKey))];
  const selfCount = entries.filter((e) => e.selfService).length;

  const rows = entries.map((e) => {
    const time = String(e.sk ?? "").slice(11, 19) || "—";
    const sep = e.principalKey.indexOf(":");
    const kind = sep < 0 ? "unknown" : e.principalKey.slice(0, sep);
    const name = sep < 0 ? e.principalKey : e.principalKey.slice(sep + 1);

    const change = el("td", { class: "num", style: "text-align:left" });
    if (e.before === undefined && e.after === undefined) change.append("—");
    else if (e.before === undefined) change.append(String(e.after));
    else change.append(String(e.before), el("span", { class: "muted" }, " → "), String(e.after));

    const by = el("td", { class: "muted" }, e.actor);
    if (e.selfService) {
      by.append(el("span", { class: "badge badge-danger", style: "margin-left:8px" }, "SELF-SERVICE"));
    }

    return el("tr", {},
      el("td", { class: "muted", style: "font-variant-numeric:tabular-nums;width:82px" }, time),
      el("td", { style: "width:104px" },
        el("span", { class: "badge", style: ACTION_STYLE[e.action] ?? "" }, e.action)),
      el("td", {}, name, el("span", { class: "badge", style: "margin-left:8px" }, kind)),
      change, by,
      el("td", { class: "muted" }, e.reason));
  });

  const table = entries.length
    ? el("table", {},
        el("thead", {}, el("tr", {},
          el("th", {}, "Time"), el("th", {}, "Action"), el("th", {}, "Principal"),
          el("th", {}, "Change"), el("th", {}, "By"), el("th", {}, "Reason"))),
        el("tbody", {}, ...rows))
    : el("div", { style: "padding:48px 16px;text-align:center" },
        el("div", { style: "font-weight:500" }, "No enforcement changes on this day"),
        el("div", { class: "muted" }, "Nobody suspended, released, or altered a cap."));

  app.replaceChildren(
    nav,
    el("div", { class: "tiles" },
      tile("Actions", String(entries.length), "suspend, release and limit changes"),
      tile("Operators acting", String(actors.length),
        actors.map((a) => a.split("@")[0]).join(", ") || "—"),
      tile("Principals touched", String(principals.length),
        principals.map((p) => p.split(":").slice(1).join(":")).join(", ") || "—"),
      tile("Self-service", String(selfCount), "someone changing their own cap")),
    el("div", { class: "card" },
      el("div", { class: "card-h" }, el("span", {}, "Enforcement changes"),
        el("span", { class: "muted" }, "Newest first · times UTC")),
      el("div", { style: "padding:4px 16px 12px" },
        table,
        el("div", { class: "muted", style: "border-top:1px solid var(--divider);padding-top:10px;margin-top:4px" },
          "Kept for 400 days — unlike the suspension records, which are cleared at 00:05 UTC each night."))));
}

// ---- actions ---------------------------------------------------------------

/**
 * Atoti modal: antd `wireframe: true` anatomy — header and footer separated from
 * the body by 1px hairlines, 16px body padding, actions right-aligned with the
 * primary last. Native <dialog> so focus trapping and Escape come for free.
 *
 * Resolves the reason, or null if the operator backed out.
 */
function askReason(opts: {
  title: string; detail: string; confirmLabel: string; danger?: boolean;
}): Promise<string | null> {
  return new Promise((resolve) => {
    const input = el("input", {
      class: "input", type: "text", placeholder: "Why are you doing this?",
      style: "width:100%;height:28px",
    }) as HTMLInputElement;

    const dlg = el("dialog", { class: "modal", "aria-labelledby": "modal-title" }) as HTMLDialogElement;
    let settled = false;
    const done = (v: string | null) => {
      if (settled) return;
      settled = true;
      resolve(v);
      dlg.close();
      dlg.remove();
    };

    const cancel = el("button", { class: "btn" }, "Cancel");
    cancel.addEventListener("click", () => done(null));
    const confirm = el("button",
      { class: opts.danger ? "btn btn-primary btn-danger" : "btn btn-primary" }, opts.confirmLabel);
    confirm.addEventListener("click", () => done(input.value.trim() || "via dashboard"));

    input.addEventListener("keydown", (e) => {
      if ((e as KeyboardEvent).key === "Enter") { e.preventDefault(); confirm.click(); }
    });
    // Escape and backdrop clicks must resolve too, or the promise never settles.
    dlg.addEventListener("cancel", (e) => { e.preventDefault(); done(null); });
    dlg.addEventListener("click", (e) => { if (e.target === dlg) done(null); });

    dlg.append(
      el("div", { class: "modal-h", id: "modal-title" }, opts.title),
      el("div", { class: "modal-b" },
        el("div", { style: "margin-bottom:8px" }, opts.detail),
        el("label", { class: "muted", style: "display:block;margin-bottom:4px" }, "Reason"),
        input,
        el("div", { class: "muted", style: "margin-top:6px" },
          "Recorded in the audit trail and emailed to bedrock-cost-alarms.")),
      el("div", { class: "modal-f" }, cancel, confirm),
    );

    document.body.append(dlg);
    dlg.showModal();
    input.focus();
  });
}

/** The inline editor from the design: presets, no browser dialogs. */
function limitPanel(row: Row, def: number, close: () => void): HTMLElement {
  const send = async (label: string, body: Record<string, unknown>) => {
    const reason = await askReason({
      title: "Raise limit",
      detail: `${label} for ${row.name} on ${currentDay} UTC. Expires at 00:05 UTC.`,
      confirmLabel: "Set limit",
    });
    if (reason === null) return;
    const r = await postJSON("/api/limit", { key: row.key, reason, ...body });
    banner(r.released
      ? `Limit raised for ${row.name} — the active suspension was lifted.`
      : `Limit updated for ${row.name}.`);
    close();
    await renderDay(currentDay);
  };

  const preset = (label: string, detail: string, body: Record<string, unknown>, cls = "btn") => {
    const b = el("button", { class: cls }, label);
    b.addEventListener("click", () => void send(detail, body).catch(actionFailed));
    return b;
  };

  const closeBtn = el("button", { class: "btn btn-ghost" }, "Close");
  closeBtn.addEventListener("click", close);

  return el("div", { class: "panel" },
    el("span", { class: "muted" }, `Temporary limit for ${row.name} on ${currentDay}`),
    el("div", { style: "display:flex;gap:4px;flex-wrap:wrap" },
      preset("$150", "Set the cap to $150.00", { limitUsd: 150 }),
      preset("$250", "Set the cap to $250.00", { limitUsd: 250 }),
      preset("$500", "Set the cap to $500.00", { limitUsd: 500 }),
      preset("No cap today", "Remove the cap entirely", { noCap: true }),
      preset(`Reset to ${money(def)}`, `Restore the default ${money(def)} cap`, { reset: true }, "btn btn-ghost")),
    el("span", { class: "muted" }, "Expires at 00:05 UTC"),
    closeBtn);
}

async function doKill(row: Row): Promise<void> {
  const suspended = row.suspended;
  const reason = await askReason({
    title: suspended ? "Release suspension" : "Kill switch",
    detail: suspended
      ? `Lift the suspension on ${row.name}, allowing Bedrock again immediately.`
      : `Deny Bedrock to ${row.name} for the rest of ${currentDay} UTC. Clears at 00:05 UTC.`,
    confirmLabel: suspended ? "Release" : "Deny Bedrock",
    danger: !suspended,
  });
  if (reason === null) return;
  await postJSON(suspended ? "/api/release" : "/api/suspend", { key: row.key, reason });
  banner(suspended
    ? `${row.name} released — Bedrock allowed again.`
    : `${row.name} suspended — Bedrock denied until 00:05 UTC.`);
  await renderDay(currentDay);
}

boot().catch(fatal);
